import React, { useState } from 'react';
import { Card, Button, Form, Spinner } from 'react-bootstrap';
import CurrencyInput from '../common/CurrencyInput';
import { calculatorApi } from '../../services/apiService';
import { TransferBondResult } from '../../types/calculator.types';
import { formatCurrency } from '../../utils/formatters';

const TransferBondCalc: React.FC = () => {
  const [transfer, setTransfer] = useState<number>(0);
  const [bond, setBond] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [contactNumber, setContactNumber] = useState<string>('');
  const [buyingAProperty, setBuyingAProperty] = useState<string | null>(null);
  const [offerMade, setOfferMade] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [emailSending, setEmailSending] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [result, setResult] = useState<TransferBondResult | null>(null);

  const [errors, setErrors] = useState<{
    transfer?: string;
    bond?: string;
    name?: string;
    email?: string;
  }>({});

  const validateCalculation = (): boolean => {
    const newErrors: typeof errors = {};
    if (!transfer || transfer <= 0) {
      newErrors.transfer = 'Please enter transfer amount';
    }
    if (!bond || bond <= 0) {
      newErrors.bond = 'Please enter bond amount';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateContactInfo = (): boolean => {
    const newErrors: typeof errors = {};
    if (!name) {
      newErrors.name = 'Please enter your name';
    }
    if (!email) {
      newErrors.email = 'Please enter your email';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Invalid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateCalculation()) return;

    setLoading(true);
    setResult(null);
    setEmailSending(false);
    setEmailSent(false);

    try {
      const data = await calculatorApi.calculateTransferBond({
        transfer,
        bond,
      });

      const newResult: TransferBondResult = {
        transfer: {
          transferDuty: data.transfer.transferDuty,
          deedsOfficeFee: data.transfer.deedsOfficeLevy,
          attorneyFee: data.transfer.attorneyFees,
          deedsOfficeSearchFee: data.transfer.deedsOfficeSearch,
          postAndPetties: data.transfer.postagesAndPetties,
          vat: data.transfer.vat,
          subTotal: data.transfer.total,
        },
        bond: {
          deedsOfficeFee: data.bond.deedsOfficeLevy,
          attorneyFee: data.bond.attorneyFees,
          deedsOfficeSearchFee: data.bond.deedsOfficeSearch,
          postAndPetties: data.bond.postagesAndPetties,
          vat: data.bond.vat,
          subTotal: data.bond.total,
        },
        total: data.transfer.total + data.bond.total,
        updatedOn: data.updatedAt,
        transferAmount: transfer,
        bondAmount: bond,
      };

      setResult(newResult);

      // Scroll to result
      setTimeout(() => {
        document.getElementById('calc-result')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!validateContactInfo()) {
      return;
    }

    setEmailSending(true);

    try {
      await calculatorApi.emailPdf({
        transfer: result!.transferAmount,
        bond: result!.bondAmount,
        name,
        email,
        contactNumber: contactNumber || undefined,
        buyingAProperty: buyingAProperty || undefined,
        offerMade: offerMade || undefined,
      });

      setEmailSent(true);
    } catch (error) {
      console.error(error);
    } finally {
      setEmailSending(false);
    }
  };

  const handleDownload = () => {
    if (!validateContactInfo()) {
      return;
    }

    calculatorApi.downloadPdf({
      transfer: result!.transferAmount,
      bond: result!.bondAmount,
      name,
      email,
      contactNumber: contactNumber || undefined,
      buyingAProperty: buyingAProperty || undefined,
      offerMade: offerMade || undefined,
    });
  };

  return (
    <Card.Body>
      <Form onSubmit={handleSubmit}>
        <h6 className="text-black-50 text-uppercase" style={{ fontSize: '13px' }}>
          Transfer
        </h6>
        <CurrencyInput
          value={transfer}
          onChange={setTransfer}
          label="Purchase Price (R)"
          required
          isInvalid={!!errors.transfer}
          errorMessage={errors.transfer}
        />

        <h6 className="text-black-50 text-uppercase" style={{ fontSize: '13px' }}>
          Bond
        </h6>
        <CurrencyInput
          value={bond}
          onChange={setBond}
          label="Bond Amount (R)"
          required
          isInvalid={!!errors.bond}
          errorMessage={errors.bond}
        />

        <div className="d-grid mb-3">
          <Button
            type="submit"
            variant="secondary"
            disabled={loading}
          >
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              CALCULATING...
            </>
          ) : (
            <>
              <i className="lni lni-calculator"></i> CALCULATE
            </>
          )}
          </Button>
        </div>
      </Form>

      {result && (
        <div id="calc-result">
          <div className="py-3"></div>

          {/* Transfer Costs */}
          <div className="border p-3">
            <div className="row border-bottom">
              <div className="col-auto">
                <h6 className="text-primary font-weight-bold">Transfer Costs</h6>
              </div>
              <div className="col text-end text-primary font-weight-bold" style={{ fontSize: '13px' }}>
                R {formatCurrency(result.transferAmount)}
              </div>
            </div>
            <div className="row">
              <div className="col">
                <table className="table table-sm mt-2 w-100">
                  <thead>
                    <tr>
                      <th colSpan={2} className="text-black-50 border-0" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                        GOVERNMENT COSTS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border-0">Transfer Duty</td>
                      <td className="text-end border-0">{formatCurrency(result.transfer.transferDuty)}</td>
                    </tr>
                    <tr>
                      <td className="border-0">Deeds Office Fee</td>
                      <td className="text-end border-0">{formatCurrency(result.transfer.deedsOfficeFee)}</td>
                    </tr>
                  </tbody>
                </table>

                <table className="table table-sm mt-2 w-100">
                  <thead>
                    <tr>
                      <th colSpan={2} className="text-black-50 border-0" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                        ATTORNEYS COSTS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border-0">Attorney Fee</td>
                      <td className="text-end border-0">{formatCurrency(result.transfer.attorneyFee)}</td>
                    </tr>
                    <tr>
                      <td className="border-0">Deeds Office Search Fee</td>
                      <td className="text-end border-0">{formatCurrency(result.transfer.deedsOfficeSearchFee)}</td>
                    </tr>
                    <tr>
                      <td className="border-0">Post & Petties</td>
                      <td className="text-end border-0">{formatCurrency(result.transfer.postAndPetties)}</td>
                    </tr>
                    <tr>
                      <td className="border-0">VAT</td>
                      <td className="text-end border-0">{formatCurrency(result.transfer.vat)}</td>
                    </tr>
                    <tr>
                      <td className="bg-light border-0">Sub Total</td>
                      <td className="bg-light text-end border-0 font-weight-bold">
                        R {formatCurrency(result.transfer.subTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Bond Costs */}
          <div className="border p-3">
            <div className="row border-bottom">
              <div className="col-auto">
                <h6 className="text-primary font-weight-bold">Bond Costs</h6>
              </div>
              <div className="col text-end text-primary font-weight-bold" style={{ fontSize: '13px' }}>
                R {formatCurrency(result.bondAmount)}
              </div>
            </div>
            <div className="row">
              <div className="col">
                <table className="table table-sm mt-2 w-100">
                  <thead>
                    <tr>
                      <th colSpan={2} className="text-black-50 border-0" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                        GOVERNMENT COSTS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border-0">Deeds Office Fee</td>
                      <td className="text-end border-0">{formatCurrency(result.bond.deedsOfficeFee)}</td>
                    </tr>
                  </tbody>
                </table>

                <table className="table table-sm mt-2 w-100">
                  <thead>
                    <tr>
                      <th colSpan={2} className="text-black-50 border-0" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                        ATTORNEYS COSTS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border-0">Attorney Fee</td>
                      <td className="text-end border-0">{formatCurrency(result.bond.attorneyFee)}</td>
                    </tr>
                    <tr>
                      <td className="border-0">Deeds Office Search Fee</td>
                      <td className="text-end border-0">{formatCurrency(result.bond.deedsOfficeSearchFee)}</td>
                    </tr>
                    <tr>
                      <td className="border-0">Post & Petties</td>
                      <td className="text-end border-0">{formatCurrency(result.bond.postAndPetties)}</td>
                    </tr>
                    <tr>
                      <td className="border-0">VAT</td>
                      <td className="text-end border-0">{formatCurrency(result.bond.vat)}</td>
                    </tr>
                    <tr>
                      <td className="bg-light border-0">Sub Total</td>
                      <td className="bg-light text-end border-0 font-weight-bold">
                        R {formatCurrency(result.bond.subTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Total */}
          <div className="bg-primary p-3">
            <div className="row text-white">
              <div className="col-auto">
                <h5 className="font-weight-bold m-0">TOTAL</h5>
              </div>
              <div className="col text-end">
                <h5 className="font-weight-bold m-0">R {formatCurrency(result.total)}</h5>
              </div>
            </div>
          </div>

          {/* Download/Email Form */}
          <div className="row">
            <div className="col">
              <div className="p-3 border mt-3" style={{ backgroundColor: '#F9F9F9' }}>
                <div className="text-muted pb-2 text-uppercase" style={{ letterSpacing: '1px', fontSize: '13px' }}>
                  Please enter your details to <strong>download or email</strong>
                </div>

                <Form.Group className="mb-3">
                  <div className="form-group has-float-label">
                    <Form.Control
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      isInvalid={!!errors.name}
                      required
                    />
                    <span className="required">Your Name</span>
                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <div className="form-group has-float-label">
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isInvalid={!!errors.email}
                      required
                    />
                    <span className="required">Email Address</span>
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <div className="form-group has-float-label">
                    <Form.Control
                      type="tel"
                      value={contactNumber}
                      onChange={(e) => setContactNumber(e.target.value)}
                    />
                    <span>Contact Number</span>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Are you looking to buy property currently?</Form.Label>
                  <Form.Select value={buyingAProperty || ''} onChange={(e) => setBuyingAProperty(e.target.value || null)}>
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>

                {buyingAProperty === 'Yes' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Have you made an offer on a property yet?</Form.Label>
                    <Form.Select value={offerMade || ''} onChange={(e) => setOfferMade(e.target.value || null)}>
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Form.Select>
                  </Form.Group>
                )}

                <div className="row">
                  <div className="col-12 my-1">
                    <div className="d-grid">
                      <Button
                        variant={emailSent ? 'primary' : 'secondary'}
                        onClick={handleSendEmail}
                        disabled={emailSending || emailSent}
                      >
                        {emailSending ? (
                          'SENDING...'
                        ) : emailSent ? (
                          'EMAIL SENT'
                        ) : (
                          <>
                            EMAIL <i className="lni lni-envelope" style={{ fontSize: '19px' }}></i>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="col-12 my-1">
                    <div className="d-grid">
                      <Button variant="secondary" onClick={handleDownload}>
                        DOWNLOAD <i className="lni lni-download" style={{ fontSize: '19px' }}></i>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="row">
            <div className="col pt-5 text-muted">
              <h6 className="text-black-50 text-uppercase" style={{ fontSize: '13px' }}>
                Disclaimer
              </h6>
              <ul style={{ fontSize: '11px' }}>
                <li>Updated on {result.updatedOn}.</li>
                <li>
                  These fees are merely to be used as a guideline and are not the exhaustive list of
                  possible charges one could be held liable to pay. Should you wish to calculate the
                  exact amounts payable kindly contact our offices and request a formal quote.
                </li>
                <li>
                  In addition to these fees, there will be additional fees/disbursements that are
                  payable such as obtaining rates, levies, Transfer Duty and HOA figures, FICA costs,
                  Conveyancers Certificates, Powers Of attorney, Undertakings, etc which may apply to
                  your transaction. These costs have not been included herein and will be in addition
                  to that set out above. Please anticipate these additional costs.
                </li>
                <li>
                  The instalment and income required are merely approximated values. Every Financial
                  institution will have its own credit criteria which will have to be met in addition
                  to any applicable legislation. Please consult your credit provider for exact amounts
                  should you require the same.
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </Card.Body>
  );
};

export default TransferBondCalc;
